<template>
  <div class="investSummary">
    <div v-if="!currentPlanId">
      <v-alert outlined dense type="info"> Please, select a plan first to view data </v-alert>
    </div>
    <v-tabs v-else v-model="currentTab">
      <v-tab>Plan</v-tab>
      <v-tab-item>
        <v-form v-model="isPlanFormValid" ref="planForm" lazy-validation>
          <v-row class="mt-2 mb-4">
            <v-col cols="4">
               <div style="max-width:300px; display:flex">
              <DatePicker v-model="statusDate" label="Status date" :rules="rules" />
              <v-btn class="mt-1" depressed color="primary" tile @click="onGo">Go</v-btn>
              </div>
            </v-col>
          </v-row>
        </v-form>
        <div v-if="loading">
          <v-progress-circular
            :size="20"
            color="primary"
            class="mr-2"
            indeterminate
          ></v-progress-circular>
          Loading...
        </div>
        <div v-else class="planChart">
          <apexchart
            width="500"
            type="pie"
            :options="planOptions"
            :series="planOptions.series"
          ></apexchart>
        </div>
      </v-tab-item>

      <v-tab>Investment</v-tab>
      <v-tab-item>
        <v-form v-model="isInvestFormValid" ref="investForm" lazy-validation>
          <v-row class="mt-2 mb-4">
              <v-col>
              <div style="max-width:300px; display:flex">
              <DatePicker v-model="investDate" label="Investment date" :rules="rules" />
              <v-btn class="mt-1" depressed color="primary" tile @click="onGo">Go</v-btn>
              </div>
              </v-col>

          </v-row>
        </v-form>
        <div v-if="loading">
          <v-progress-circular
            :size="20"
            color="primary"
            class="mr-2"
            indeterminate
          ></v-progress-circular>
          Loading...
        </div>
        <div v-else class="investChart">
          <v-data-table
            dense
            :headers="investHeaders"
            :items="investSummary"
            :items-per-page="-1"
            item-key="PortfolioId"
          >
            <template
              v-for="header in investHeaders.filter((header) => header.format !== '')"
              v-slot:[`item.${header.value}`]="{ item, value }"
            >
              {{ formatTableEl(item, value, header.format) }}
            </template>
            <template v-if="investSummary.length" slot="body.append">
              <tr>
                <td><strong>Investment totals</strong></td>
                <td></td>
                <td></td>
                <td :style="{ textAlign: 'right' }">
                  <strong>{{ valueTotals }}</strong>
                </td>
                <td :style="{ textAlign: 'right' }">
                  <strong>{{ contsTotals }}</strong>
                </td>
                <td></td>
              </tr>
            </template>
          </v-data-table>
          <div v-if="assetOptions && investOptions" class="investCharts">
            <v-row>
              <v-col cols="6" xl="4">
                <b>By Asset</b>
              </v-col>
              <v-col cols="6" xl="4">
                <b>By Fund</b>
              </v-col>
            </v-row>
            <v-row class="pb-6">
              <v-col cols="6" xl="4">
                <apexchart
                  type="pie"
                  width="500"
                  height="400"
                  :options="assetOptions"
                  :series="assetOptions.series"
                  class="mr-10"
                ></apexchart>
              </v-col>
              <v-col cols="6" xl="4">
                <apexchart
                  type="pie"
                  width="500"
                  height="400"
                  :options="investOptions"
                  :series="investOptions.series"
                >
                </apexchart>
              </v-col>
            </v-row>
          </div>
        </div>
      </v-tab-item>
    </v-tabs>
    <v-footer padless fixed outlined class="pl-5 pr-5 pt-3 pb-3" color="white" elevation="3">
      <v-spacer></v-spacer>
      <v-btn depressed color="primary" :disabled="!currentPlanId" @click="onGo"> Go </v-btn>
    </v-footer>
  </div>
</template>

<script>
import DatePicker from '../../components/formControls/DatePicker.vue';
import Utils from '@/utils';

export default {
  name: 'InvestSummary',
  components: { DatePicker },

  data() {
    return {
      loading: false,
      rules: [(v) => !!v || 'Required.'],
      formMaxWidth: 500,
      currentTab: 0,

      statusDate: new Date().toISOString(),
      memberSummary: [],
      isPlanFormValid: false,
      planOptions: {},

      investDate: new Date().toISOString(),
      isInvestFormValid: false,
      investSummary: [],
      investHeaders: [
        { text: 'Manager', value: 'ManagerName' },
        { text: 'Fund', value: 'FundName' },
        { text: 'Total units', value: 'Units', align: 'end', format: '{0:0.0000}' },
        { text: 'Total value', value: 'Value', align: 'end', format: '{0:0.00}' },
        { text: 'Total contributions', value: 'Contributions', align: 'end', format: '{0:0.00}' },
        { text: 'Price', value: 'BidPrice', align: 'end', format: '{0:0.0000}' },
      ],
    };
  },

  computed: {
    currentPlanId() {
      return this.$store.getters.currentPlanId;
    },

    valueTotals() {
      if (this.investSummary.length) {
        const total = this.investSummary.reduce((total, current) => (total += current.Value), 0);
        return this.formatTableEl(null, total, '{0:00.00}');
      }
      return null;
    },

    contsTotals() {
      if (this.investSummary.length) {
        const total = this.investSummary.reduce(
          (total, current) => (total += current.Contributions),
          0
        );
        return this.formatTableEl(null, total, '{0:00.00}');
      }
      return null;
    },

    assetOptions() {
      const labelsSet = new Set();
      const series = [];
      if (this.investSummary.length) {
        this.investSummary.forEach((s) => {
          labelsSet.add(s.AssetClass);
        });
        const labels = Array.from(labelsSet);
        labels.forEach((label) => {
          series.push(
            this.investSummary.reduce((total, current) => {
              if (current.AssetClass === label) {
                total += current.Value;
              }
              return Math.round(total * 100) / 100;
            }, 0)
          );
        });

        return { labels, series };
      }
      return null;
    },

    investOptions() {
      if (this.investSummary.length) {
        const labels = this.investSummary.map((s) => s.ManagerName + '/' + s.FundName);
        const series = this.investSummary.map((s) => Math.round(s.Value * 100) / 100);

        return { labels, series };
      }
      return null;
    },
  },

  methods: {
    formatTableEl(item, value, format) {
      return Utils.formatTableValue(value, format);
    },

    onGo() {
      if (this.currentTab === 0) {
        this.isPlanFormValid = this.$refs.planForm.validate();
        if (this.isPlanFormValid) this.getMemberSummary();
      } else if (this.currentTab === 1) {
        this.isInvestFormValid = this.$refs.investForm.validate();
        if (this.isInvestFormValid) this.getInvestmentSummary();
      }
    },

    async getMemberSummary() {
      this.loading = true;
      try {
        const response = await this.$api.get(
          `/Summary/GetMemberSummary?planId=${this.currentPlanId}&statusDate=${this.statusDate}`
        );

        this.memberSummary = response.data;
        if (this.memberSummary.length) {
          this.planOptions = {
            labels: this.memberSummary.map((s) => s.Description),
            dataLabels: {
              formatter: function (val, opts) {
                return opts.w.config.series[opts.seriesIndex];
              },
            },
            series: this.memberSummary.map((s) => s.count),
          };
        }
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },

    async getInvestmentSummary() {
      this.loading = true;
      try {
        const response = await this.$api.get(
          `/Summary/GetInvestmentSummary?planId=${this.currentPlanId}&investDate=${this.investDate}`
        );
        this.investSummary = response.data;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.investSummary {
  margin-bottom: 20px;
  padding-left: 30px;
  padding-right: 30px;

  .v-tabs {
    margin-bottom: 10px;

    .v-tabs-slider-wrapper {
      width: 0px !important;
      height: 0px !important;
    }

    .v-tab {
      border: 1px solid;
      border-radius: 30px;
      color: #4a1cd4 !important;
      height: 35px;
      margin-right: 10px;

      &.v-tab--active {
        color: white !important;
        background-color: #4a1cd4;
      }
    }
  }
  .v-data-table tbody tr {
    cursor: default;
  }
}
</style>
